import { Divider, Menu, MenuItem } from '@material-ui/core';
import { useFormikEnhanced } from '@superdispatch/forms';
import { useSnackbarStack } from '@superdispatch/ui';
import { Button, ButtonProps } from '@superdispatch/ui-lab';
import { useEffect, useState } from 'react';
import { logInfo } from 'shared/helpers/Logger';
import { AddLoadAlertIcon } from 'shared/icons/AddLoadAlertIcon';
import {
  composeSavedSearchTitle,
  PostingSearchCriteriaDTO,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { Prompt } from 'shared/routing/NavigationBlock';
import styled from 'styled-components';
import {
  AlertDuration,
  alertDurationOptions,
  formatAlertCreateMessage,
  PostingSavedSearchDTO,
  PostingSavedSearchPayloadDTO,
  postingSavedSearchPayloadSchema,
} from '../../data/dto/PostingSavedSearchDTO';
import { LoadSearchSource } from '../../data/dto/PostingsPageParamsDTO';
import {
  useCreateSavedSearch,
  useSavedSearches,
} from '../../data/LoadboardSavedSearchesAPI';
import { trackLoadboardEvent } from '../LoadboardAnalytics';
import { usePostingsPageContext } from './PostingsPageContext';

const StyledMenuItem = styled(MenuItem)`
  padding: 6px 16px;
`;

const StyledMenu = styled(Menu)`
  margin-top: 4px;
`;

const StyledDivider = styled(Divider)`
  margin: 8px 0;
`;

interface Props extends ButtonProps {
  source: LoadSearchSource;
  title?: string;
  searchCriteria?: PostingSearchCriteriaDTO;
  onClose?: () => void;
}

export function QuickLoadAlertButton({
  source,
  onClick,
  onClose,
  title = 'Create Load Alert',
  searchCriteria,
  ...props
}: Props) {
  const { addSnackbar } = useSnackbarStack();
  const { params, setParams } = usePostingsPageContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const savedSearches = useSavedSearches();
  const criteria = searchCriteria || params.search_criteria;

  const { mutateAsync: createSavedSearch } = useCreateSavedSearch();

  const createSavedSearchForm = useFormikEnhanced<
    { alert_duration: AlertDuration },
    PostingSavedSearchDTO
  >({
    initialValues: { alert_duration: 'week' },
    onSubmit: ({ alert_duration }) => {
      setAnchorEl(null);

      const payload = postingSavedSearchPayloadSchema.cast({
        alert_duration,
        alert_enabled: true,
        search_criteria: criteria,
        description: composeSavedSearchTitle(criteria),
      }) as PostingSavedSearchPayloadDTO;

      logInfo('Create saved search', { payload });
      return createSavedSearch(payload).then((response) => {
        trackLoadboardEvent({
          name: 'SLB: Created Load Alert',
          source,
          savedSearch: response,
        });
        return response;
      });
    },
    onSubmitSuccess({ search_criteria }, { alert_duration }) {
      setParams({ search_criteria });
      onClose?.();
      void savedSearches.refetch();
      addSnackbar(formatAlertCreateMessage(alert_duration));
    },
    onSubmitFailure(error) {
      addSnackbar(error.message, { variant: 'error' });
    },
  });

  function customizeSavedSearch() {
    onClose?.();
    setParams({
      utm_medium: source,
      load_alert: 'create',
      search_criteria: criteria,
      current_load_guid: undefined,
    });
  }

  useEffect(() => {
    if (
      !savedSearches.isLoading &&
      createSavedSearchForm.status.type === 'submitted' &&
      // Wait until saved search is fully loaded
      savedSearches.data?.some(
        (x) =>
          createSavedSearchForm.status.type === 'submitted' &&
          x.guid === createSavedSearchForm.status.payload.guid,
      )
    ) {
      setParams({
        current_saved_search_guid: createSavedSearchForm.status.payload.guid,
      });
      createSavedSearchForm.setStatus({ type: 'initial' });
    }
  }, [setParams, savedSearches, createSavedSearchForm]);

  return (
    <>
      <Prompt
        when={
          createSavedSearchForm.status.type === 'initial' &&
          createSavedSearchForm.isSubmitting
        }
        message="Changes have not been saved. Leaving will result in unsaved changes being lost."
      />
      <Button
        {...props}
        fullWidth={props.fullWidth ?? true}
        pending={createSavedSearchForm.isSubmitting}
        startIcon={<AddLoadAlertIcon />}
        onClick={(event) => {
          onClick?.(event);

          if (
            criteria.pickup_venues.length ||
            criteria.delivery_venues.length
          ) {
            setAnchorEl(event.currentTarget);
          } else {
            customizeSavedSearch();
          }

          trackLoadboardEvent({
            name: 'SLB: Clicked Create Load Alert',
            source,
          });
        }}
      >
        {title}
      </Button>

      <StyledMenu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        <StyledMenuItem disabled={true}>Alert Duration</StyledMenuItem>

        {Object.entries(alertDurationOptions).map(([value, label]) => (
          <StyledMenuItem
            key={value}
            onClick={() => {
              void createSavedSearchForm.setValues({
                alert_duration: value as AlertDuration,
              });
              createSavedSearchForm.handleSubmit();
            }}
          >
            {label}
          </StyledMenuItem>
        ))}

        <StyledDivider />

        <StyledMenuItem
          onClick={() => {
            customizeSavedSearch();
            setAnchorEl(null);
          }}
        >
          Customize Load Alert
        </StyledMenuItem>
      </StyledMenu>
    </>
  );
}
